import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';

const { logger } = Sentry;
import { getResumeOptimizationReport, GetAccessTokenSilently } from '../../services/api';
import './ResumeOptimizationView.scss';

interface OptimizationReport {
  aboutMe?: {
    original: string;
    optimized: string;
    reason: string;
  };
  experiences?: Array<{
    original: any;
    optimized: any;
    reason: string;
    chunkIndex?: number;
  }>;
  skills?: {
    original: string[];
    optimized: string[];
    reason: string;
  };
  educations?: Array<{
    original: any;
    optimized: any;
    reason: string;
  }>;
}

interface ResumeOptimizationViewProps {
  downloadUrl: string | null;
  reportUrl: string | null;
  onClose: () => void;
  jobTitle?: string;
  companyName?: string;
  getAccessTokenSilently: GetAccessTokenSilently;
}

const ResumeOptimizationView: React.FC<ResumeOptimizationViewProps> = ({
  downloadUrl,
  reportUrl,
  onClose,
  jobTitle,
  companyName,
  getAccessTokenSilently
}) => {
  const [report, setReport] = useState<OptimizationReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReport();
  }, [getAccessTokenSilently]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch report from API endpoint instead of S3 directly
      const data = await getResumeOptimizationReport(getAccessTokenSilently);
      setReport(data);
    } catch (err) {
      logger.error('Error fetching optimization report', {
        error: err instanceof Error ? err.message : String(err),
      });
      const errorMessage = err instanceof Error ? err.message : 'Failed to load optimization report';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (downloadUrl) {
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${jobTitle || 'Resume'}_${companyName || 'Resume'}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const formatExperience = (exp: any) => {
    if (!exp) return '';
    const parts = [];
    if (exp.title) parts.push(exp.title);
    if (exp.company) parts.push(`at ${exp.company}`);
    if (exp.location) parts.push(exp.location);
    return parts.join(' ') || 'Experience';
  };

  const renderPoints = (points: string[] | undefined) => {
    if (!points || points.length === 0) return <span className="empty-content">No points</span>;
    return (
      <ul className="points-list">
        {points.map((point, idx) => (
          <li key={idx}>{point}</li>
        ))}
      </ul>
    );
  };

  const hasContent = (value: any): boolean => {
    if (!value) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') {
      // Check if object has any meaningful content
      if (value.points && Array.isArray(value.points) && value.points.length > 0) return true;
      if (value.description && typeof value.description === 'string' && value.description.trim().length > 0) return true;
      return Object.keys(value).length > 0;
    }
    return false;
  };

  const hasAboutMeContent = (aboutMe: any): boolean => {
    if (!aboutMe) return false;
    return hasContent(aboutMe.original) || hasContent(aboutMe.optimized);
  };

  const hasSkillsContent = (skills: any): boolean => {
    if (!skills) return false;
    return hasContent(skills.original) || hasContent(skills.optimized);
  };

  const hasExperienceContent = (exp: any): boolean => {
    if (!exp) return false;
    return hasContent(exp.original?.points) || 
           hasContent(exp.original?.description) ||
           hasContent(exp.optimized?.points) || 
           hasContent(exp.optimized?.description);
  };

  const hasEducationContent = (edu: any): boolean => {
    if (!edu) return false;
    return hasContent(edu.original) || hasContent(edu.optimized);
  };

  return (
    <div className="resume-optimization-view-overlay">
      <div className="resume-optimization-view">
        <div className="resume-optimization-header">
          <div className="header-content">
            <h2>Resume Optimization Report</h2>
            <p className="header-subtitle">Review the improvements made to your resume</p>
          </div>
          <button className="close-button" onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="resume-optimization-content">
          <div className="download-section">
            <div className="download-disclaimer">
              <svg className="icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5M2 12l10 5 10-5"></path>
              </svg>
              <div className="disclaimer-text">
                <strong>Please verify</strong> that all information in the resume is correct before submitting it to employers. You can make any necessary changes using Microsoft Word or Google Docs.
              </div>
            </div>
            <button className="download-button" onClick={handleDownload} disabled={!downloadUrl}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              Download Resume
            </button>
          </div>

          {loading && (
            <div className="loading">Loading optimization report...</div>
          )}

          {error && (
            <div className="error">{error}</div>
          )}

          {!loading && !error && report && (
            <div className="optimization-sections">
              {/* About Me Section */}
              {report.aboutMe && hasAboutMeContent(report.aboutMe) && (
                <div className="optimization-section">
                  <div className="section-header">
                    <h3>About Me</h3>
                    <span className="section-badge">Professional Summary</span>
                  </div>
                  <div className="comparison">
                    <div className="comparison-column original">
                      <div className="column-header">
                        <span className="column-label">Original</span>
                      </div>
                      <div className="content">{report.aboutMe.original || <span className="empty-content">No content</span>}</div>
                    </div>
                    <div className="comparison-column optimized">
                      <div className="column-header">
                        <span className="column-label">Optimized</span>
                        <span className="improvement-badge">Improved</span>
                      </div>
                      <div className="content">{report.aboutMe.optimized || <span className="empty-content">No content</span>}</div>
                    </div>
                  </div>
                  {report.aboutMe.reason && (
                    <div className="reason">
                      <div className="reason-header">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="16" x2="12" y2="12"></line>
                          <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                        <span>Why this was changed</span>
                      </div>
                      <p>{report.aboutMe.reason}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Experiences Section */}
              {report.experiences && report.experiences.length > 0 && 
               report.experiences.some(exp => hasExperienceContent(exp)) && (
                <div className="optimization-section">
                  <div className="section-header">
                    <h3>Work Experience</h3>
                  </div>
                  {report.experiences
                    .filter(exp => hasExperienceContent(exp))
                    .map((exp, idx) => (
                      <div key={idx} className="experience-item">
                        <h4 className="experience-title">{formatExperience(exp.original)}</h4>
                        <div className="comparison">
                          <div className="comparison-column original">
                            <div className="column-header">
                              <span className="column-label">Original</span>
                            </div>
                            <div className="content">
                              {renderPoints(exp.original?.points)}
                              {!exp.original?.points && exp.original?.description && (
                                <div>{exp.original.description}</div>
                              )}
                            </div>
                          </div>
                          <div className="comparison-column optimized">
                            <div className="column-header">
                              <span className="column-label">Optimized</span>
                              <span className="improvement-badge">Improved</span>
                            </div>
                            <div className="content">
                              {renderPoints(exp.optimized?.points)}
                              {!exp.optimized?.points && exp.optimized?.description && (
                                <div>{exp.optimized.description}</div>
                              )}
                            </div>
                          </div>
                        </div>
                        {exp.reason && (
                          <div className="reason">
                            <div className="reason-header">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                              </svg>
                              <span>Why this was changed</span>
                            </div>
                            <p>{exp.reason}</p>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}

              {/* Skills Section */}
              {report.skills && hasSkillsContent(report.skills) && (
                <div className="optimization-section">
                  <div className="section-header">
                    <h3>Skills</h3>
                  </div>
                  <div className="comparison">
                    <div className="comparison-column original">
                      <div className="column-header">
                        <span className="column-label">Original</span>
                      </div>
                      <div className="content">
                        {report.skills.original && report.skills.original.length > 0 ? (
                          <ul className="skills-list">
                            {report.skills.original.map((skill, idx) => (
                              <li key={idx}>{skill}</li>
                            ))}
                          </ul>
                        ) : (
                          <span className="empty-content">No skills</span>
                        )}
                      </div>
                    </div>
                    <div className="comparison-column optimized">
                      <div className="column-header">
                        <span className="column-label">Optimized</span>
                        <span className="improvement-badge">Improved</span>
                      </div>
                      <div className="content">
                        {report.skills.optimized && report.skills.optimized.length > 0 ? (
                          <ul className="skills-list">
                            {report.skills.optimized.map((skill, idx) => (
                              <li key={idx}>{skill}</li>
                            ))}
                          </ul>
                        ) : (
                          <span className="empty-content">No skills</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {report.skills.reason && (
                    <div className="reason">
                      <div className="reason-header">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="16" x2="12" y2="12"></line>
                          <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                        <span>Why this was changed</span>
                      </div>
                      <p>{report.skills.reason}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Educations Section */}
              {report.educations && report.educations.length > 0 && 
               report.educations.some(edu => hasEducationContent(edu)) && (
                <div className="optimization-section">
                  <div className="section-header">
                    <h3>Education</h3>
                  </div>
                  {report.educations
                    .filter(edu => hasEducationContent(edu))
                    .map((edu, idx) => (
                      <div key={idx} className="education-item">
                        <h4 className="education-title">
                          {edu.original?.degree || ''} 
                          {edu.original?.field && ` in ${edu.original.field}`}
                          {edu.original?.school && ` - ${edu.original.school}`}
                        </h4>
                        {edu.reason && (
                          <div className="reason">
                            <div className="reason-header">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                              </svg>
                              <span>Why this was changed</span>
                            </div>
                            <p>{edu.reason}</p>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}

              {!hasAboutMeContent(report.aboutMe) && 
               (!report.experiences || !report.experiences.some(exp => hasExperienceContent(exp))) && 
               (!hasSkillsContent(report.skills)) && 
               (!report.educations || !report.educations.some(edu => hasEducationContent(edu))) && (
                <div className="no-changes">
                  <p>No optimization changes were made to this resume.</p>
                </div>
              )}
            </div>
          )}

          {!loading && !error && !report && (
            <div className="no-report">
              <p>No optimization report available for this resume.</p>
            </div>
          )}
        </div>

        <div className="resume-optimization-footer">
          <button className="close-button-bottom" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResumeOptimizationView;

